import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import axios, { AxiosError } from 'axios';

import { encrypt } from '../../../../../utils/security.helper';
import { REACT_APP_LOGIN_AUTH } from '../../../../../constants/envConstants';

import { extractPinVerificationDigit } from './helper/helper';
import Mpin from './Mpin';
import {
  ResetMpinFormValues,
  ResetMpinProps,
  UpdateMpinResponse,
} from './Interface/reset-mpin.interface';

const MPIN_LENGTH = 4;

const validationSchema = Yup.object({
  password: Yup.string().required('Password is required'),
  m_pin: Yup.string()
    .matches(/^\d+$/, 'PIN must contain digits only')
    .length(4, 'Pin must be 4 digits')
    .required('Pin is required'),
  confirm_m_pin: Yup.string()
    .oneOf([Yup.ref('m_pin')], 'PIN and Confirm PIN does not match')
    .required('Please confirm your Pin'),
});

const ResetMpin: React.FC<ResetMpinProps> = ({ handleCloseDialogue }) => {
  const toast = useRef<Toast>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showMpin, setShowMpin] = useState(true);
  const [showConfirmMpin, setShowConfirmMpin] = useState(true);

  // OTP refs (same pattern as Mpin & MpinLogin)
  const pinRef = useRef<string[]>(Array(MPIN_LENGTH).fill(''));
  const confirmPinRef = useRef<string[]>(Array(MPIN_LENGTH).fill(''));

  const formik = useFormik<ResetMpinFormValues>({
    initialValues: {
      password: '',
      m_pin: '',
      confirm_m_pin: '',
    },
    validationSchema,
    onSubmit: async values => {
      try {
        const res = await axios.put<UpdateMpinResponse>(
          `${REACT_APP_LOGIN_AUTH}/auth/update-mpin`,
          {
            password: encrypt(values.password),
            mpin: encrypt(values.m_pin),
            confirmMpin: encrypt(values.confirm_m_pin),
          }
        );

        if (res.status === 200) {
          toast.current?.show({
            severity: 'success',
            summary: 'Success',
            detail: res.data?.message ?? 'Pin updated successfully',
          });

          setTimeout(() => handleCloseDialogue(false), 1500);
        }
      } catch (err) {
        const error = err as AxiosError<{ message?: string }>;

        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail:
            error.response?.data?.message ??
            'Something went wrong while updating pin',
        });
      }
    },
  });

  /* ---------------- OTP Handlers ---------------- */

  const handleOtpChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    idx: number,
    ref: React.MutableRefObject<string[]>,
    field: 'm_pin' | 'confirm_m_pin'
  ) => {
    const resetMpinDigits = extractPinVerificationDigit(e?.target?.value);
    ref.current[idx] = resetMpinDigits;
    e.target.value = resetMpinDigits;

    formik.setFieldTouched(field, true, true);
    formik.setFieldValue(field, ref.current.join(''));

    if (resetMpinDigits && idx < MPIN_LENGTH - 1) {
      document.getElementById(`${field}-otp-${idx + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    idx: number,
    ref: React.MutableRefObject<string[]>,
    field: 'm_pin' | 'confirm_m_pin'
  ) => {
    if (e.key === 'Backspace') {
      ref.current[idx] = '';

      if (idx > 0 && !e.currentTarget.value) {
        document.getElementById(`${field}-otp-${idx - 1}`)?.focus();
      }
    }

    formik.setFieldValue(field, ref.current.join(''));
  };

  const isMpinSet = localStorage.getItem('is_mpin_enabled');

  return (
    <>
      <Toast ref={toast} />

      {isMpinSet === 'true' ? (
        <div className="w-full" style={{ background: '#f9fafb' }}>
          <Card className="w-full p-4 shadow-3 border-round-2xl">
            <h3 className="text-center mb-4 mt-0">Reset Pin</h3>

            <form onSubmit={formik.handleSubmit}>
              <div className="grid">
                {/* PASSWORD */}
                <div className="col-12">
                  <span className="p-float-label w-full relative">
                    <InputText
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className="p-inputtext-sm w-full pr-6"
                      value={formik.values.password}
                      onChange={e =>
                        formik.setFieldValue('password', e.target.value)
                      }
                    />

                    <label htmlFor="password">
                      Password<span style={{ color: 'red' }}>*</span>
                    </label>

                    <i
                      className={`pi ${showPassword ? 'pi-eye-slash' : 'pi-eye'}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowPassword(p => !p)}
                      style={{
                        position: 'absolute',
                        right: '8px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                      }}
                    />
                  </span>
                </div>

                {/* PIN */}
                <div className="col-12 mt-3">
                  <label htmlFor="m_pin" className="text-base block mb-2">
                    Pin<span style={{ color: 'red' }}>*</span>
                  </label>

                  <div className="flex align-items-center gap-2 w-full">
                    {Array.from({ length: MPIN_LENGTH }).map((_, idx) => (
                      <input
                        key={idx}
                        id={`m_pin-otp-${idx}`}
                        type={showMpin ? 'password' : 'text'}
                        value={pinRef.current[idx] || ''}
                        maxLength={1}
                        className="border-1 border-gray-400 border-round-sm
             outline-none shadow-none
             focus:outline-none focus:shadow-none focus:border-gray-400
             text-center h-3rem flex-1 min-w-0"
                        onChange={e => handleOtpChange(e, idx, pinRef, 'm_pin')}
                        onKeyDown={e =>
                          handleOtpKeyDown(e, idx, pinRef, 'm_pin')
                        }
                      />
                    ))}

                    <i
                      className={`pi ${
                        showMpin ? 'pi-eye-slash' : 'pi-eye'
                      } ml-2`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowMpin(p => !p)}
                    />
                  </div>

                  {formik.touched.m_pin && formik.errors.m_pin && (
                    <div className="text-red-500 text-xs">
                      {formik.errors.m_pin}
                    </div>
                  )}
                </div>

                {/* CONFIRM PIN */}
                <div className="col-12 mt-3">
                  <label
                    htmlFor="confirm_m_pin"
                    className="text-base block mb-2"
                  >
                    Confirm Pin<span style={{ color: 'red' }}>*</span>
                  </label>

                  <div className="flex align-items-center gap-2 w-full">
                    {Array.from({ length: MPIN_LENGTH }).map((_, idx) => (
                      <input
                        key={idx}
                        id={`confirm_m_pin-otp-${idx}`}
                        type={showConfirmMpin ? 'password' : 'text'}
                        value={confirmPinRef.current[idx] || ''}
                        maxLength={1}
                        className="border-1 border-gray-400 border-round-sm
                                outline-none shadow-none
                                focus:outline-none focus:shadow-none focus:border-gray-400
                                text-center h-3rem flex-1 min-w-0"
                        onChange={e =>
                          handleOtpChange(
                            e,
                            idx,
                            confirmPinRef,
                            'confirm_m_pin'
                          )
                        }
                        onKeyDown={e =>
                          handleOtpKeyDown(
                            e,
                            idx,
                            confirmPinRef,
                            'confirm_m_pin'
                          )
                        }
                      />
                    ))}

                    <i
                      className={`pi ${
                        showConfirmMpin ? 'pi-eye-slash' : 'pi-eye'
                      } ml-2`}
                      role="button"
                      tabIndex={0}
                      onClick={() => setShowConfirmMpin(p => !p)}
                    />
                  </div>

                  {formik.touched.confirm_m_pin &&
                    formik.errors.confirm_m_pin && (
                      <div className="text-red-500 text-xs">
                        {formik.errors.confirm_m_pin}
                      </div>
                    )}
                </div>

                {/* SUBMIT */}
                <div className="col-12 mt-4">
                  <Button
                    label="Submit"
                    type="submit"
                    className="w-full"
                    disabled={
                      !formik.values.password ||
                      formik.values.m_pin.length !== MPIN_LENGTH ||
                      formik.values.confirm_m_pin.length !== MPIN_LENGTH ||
                      formik.values.m_pin !== formik.values.confirm_m_pin
                    }
                  />
                </div>
              </div>
            </form>
          </Card>
        </div>
      ) : (
        <Mpin handleCloseDialogue={handleCloseDialogue} />
      )}
    </>
  );
};

export default ResetMpin;
